import React, { useEffect, useMemo, useState } from 'react';

import TrendsInfoTable from './TrendsInfoTable';
import { dealwithRatio, sortChannel } from '../../../utils';
import {
  channelColorMap,
  channelNameMap,
  countMap,
  metricsMap,
  metricsOptions,
  titleMap,
} from '../../../constants';
import BubbleCommon, {
  communityStreamingAvgText,
  communityStreamingPeakCCVMaxText,
} from '@/pages/opinion/components/BubbleCommon';
import {
  echartCountMat,
  handleMentionData,
  mathFormat,
} from '@/pages/opinion/utils';
import { getStreamingTrends } from '@/pages/opinion/services/community';
import LineChart from '../../../components/LineChart';
import CardCommon from '@/pages/opinion/components/CardCommon';

import { useIntl } from 'umi';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { channelInfo } from '@/pages/opinion/components/ChannelLabel';
import DownloadDialog from '@op/components/DownloadCommon/download-dialog';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { getNeedTime } from '@/pages/opinion/utils/time';
import { NO_DATE } from '@/pages/opinion/constants';

export default function TrendsInfo({
  metric,
  params,
  gameInfo,
  channelList,
}: any) {
  const { formatMessage } = useIntl();
  const { downloadLegacy, downloading } = useDownloadParams();
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [lineSeriesData, setLineSeriesData] = useState<Array<any>>([]);
  const [lineXAxisData, setLineXAxisData] = useState<Array<any>>([]);
  const [tableRecords, setTableRecords] = useState<Array<any>>([]);
  const title = useMemo(() => {
    const tips =
      metric === 'avgCCV'
        ? communityStreamingAvgText
        : metric === 'peakCCV'
        ? communityStreamingPeakCCVMaxText
        : null;

    return (
      <div className="trends-card-title">
        <span>{formatMessage({ id: 'Trends' })}</span>
        <BubbleCommon tipsMap={tips} showIcon={false}>
          <span className="trends-card-subtitle">
            ({formatMessage({ id: titleMap[metric] })}: {echartCountMat(total)})
          </span>
        </BubbleCommon>
      </div>
    );
  }, [total, formatMessage]);

  const clearRecords = () => {
    setTotal(0);
    setTableRecords([]);
    setLineXAxisData([]);
    setLineSeriesData([]);
  };

  useEffect(() => {
    if (Object.keys(params).length === 0) {
      return;
    }
    setLoading(true);
    getStreamingTrends({ ...params, metric: metric, source: 'stream' })
      .then((data: any) => {
        const dateType = params.date_type || 'daily';
        const { timeline_of_end_time, timeline_of_start_time, list, total } =
          data || {};
        if (!list || list?.length === 0) {
          clearRecords();
        } else {
          const { table, charts } = dealwithRatio(
            timeline_of_start_time,
            timeline_of_end_time,
            list?.map((item: any) => ({
              time: item.time,
              name: item.key,
              count: item.value,
            })) || [],
            dateType,
            countMap[metric],
            params.channels?.length === 0 ? channelList : params.channels,
          );
          const needTime = getNeedTime(dateType);

          const { newArr, timeArr } = handleMentionData(charts, needTime);

          setTotal(total);
          setTableRecords(table);
          setLineXAxisData(timeArr);
          sortChannel(newArr, (fun) => (a, b) => fun(a.name, b.name));
          setLineSeriesData(newArr);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [gameInfo, params, metric]);

  const download = (downloadInfo: any = {}) => {
    const group_by: Array<string> = [];
    if (downloadInfo.accumulation?.by_date) {
      group_by.push('date');
    }
    if (downloadInfo.accumulation?.by_channel) {
      group_by.push('channel');
    }
    const newParams = {
      ...params,
      ...gameInfo,
      metric_list: downloadInfo.metrics,
      group_by,
      page: 1,
      source: 'stream',
      page_size: 1000,
    };
    downloadLegacy({
      limit: 150000,
      params: newParams,
      downloadFunc: 'getStreamingDownloadTrends',
      sheetName: 'Trends',
      simpleName: 'Streaming',
    });
  };

  const [visible, setVisible] = useState(false);

  return (
    <>
      <DownloadDialog
        showList={[
          {
            label: 'Metrics',
            type: 'checkbox',
            list: metricsOptions,
          },
          {
            label: 'Calculation Method',
            type: 'radioGroup',
            list: [
              {
                label: 'Time Settings',
                list: [
                  {
                    text: 'Aggregated',
                    value: 'Aggregated',
                  },
                  {
                    text: 'Separate by Time',
                    value: 'Separate by Time',
                  },
                ],
              },
              {
                label: 'Channel Settings',
                list: [
                  {
                    text: 'Aggregated',
                    value: 'Aggregated',
                  },
                  {
                    text: 'Separate by Channel',
                    value: 'Separate by Channel',
                  },
                ],
              },
            ],
          },
        ]}
        visible={visible}
        setVisible={setVisible}
        download={download}
        gameInfo={gameInfo}
        confirmLoading={downloading}
      ></DownloadDialog>

      <CardCommon
        title={title}
        className="dbo-community-card dbo-trends-card"
        hasDownload
        handleDownloadOnClick={() => setVisible(true)}
      >
        <>
          <div className="line-echarts">
            <LineChart
              colorObj={channelColorMap}
              tooltipFormatterFunc={(_params) => {
                const letTitle = listTimeTitleFunc(
                  _params[0]?.axisValue,
                  params.date_type,
                );
                const rightTitle = formatMessage({
                  id: metricsMap[metric] || NO_DATE.EMPTY,
                });
                const config = formatChartParamsDate({
                  title: letTitle,
                  rightTitle,
                  params: _params,
                  isSort: true,
                  textShowFun: (marker: string, serName: any) => {
                    serName =
                      serName == 'facebook' ? 'facebook_gaming' : serName;
                    const { img, name } = channelInfo(serName);
                    return `<span class="chart-common-formatter-marker"> ${marker}<img src=${img} class="chart-common-formatter-img" /></span>&nbsp;${
                      name || serName
                    }`;
                  },
                  dealwithRecords: (records, total) => {
                    records.unshift({
                      text: formatMessage({ id: 'Sum' }),
                      value: mathFormat(total) as string,
                      originValue: total,
                      originText: 'Sum',
                    });
                  },
                });

                return config;
              }}
              xAxisData={lineXAxisData}
              seriesDataArray={lineSeriesData}
              lineLegendFormatter={(_name: string) => {
                const name = channelNameMap[_name];
                return `{${
                  _name === 'facebook' ? 'facebook_gaming' : _name
                }|} ${name || _name}`;
              }}
              loading={loading}
              dateType={params.date_type}
              height={350}
              isStack
              connectNulls
            ></LineChart>
          </div>

          <TrendsInfoTable
            channelList={
              params.channels?.length === 0 ? channelList : params.channels
            }
            metric={metric}
            tableList={tableRecords}
            dateType={params.date_type}
            loading={loading}
          ></TrendsInfoTable>
        </>
      </CardCommon>
    </>
  );
}
